import { defineMessages, FormattedMessage, useIntl } from "react-intl";
import { Carousel, Col, Row } from "react-bootstrap";
import Image from "next/image";
import styled from "@emotion/styled";
import Link from "next/link";
import Header from "@/components/layout/Header";
import MainContent from "@/components/layout/MainContent";
import GitHubRepoAnalysisForm from "@/components/content/GitHubRepoAnalysisForm";
import PrivateBetaForm from "@/components/content/PrivateBetaForm";
import Footer from "@/components/layout/Footer";
import PageContainer from "@/components/layout/PageContainer";

const messages = defineMessages({
  pageTitle: {
    id: "LangingPage.pageTitle",
    defaultMessage: "Keep track of hacks in your projects.",
  },
  code0ImageAlt: {
    id: "LangingPage.code0ImageAlt",
    defaultMessage:
      "An image showing code with a comment indicating a workaround to a bug in a dependency.",
  },
  code1ImageAlt: {
    id: "LangingPage.code1ImageAlt",
    defaultMessage:
      "An image showing code with a comment indicating a workaround to a bug in a dependency. The comment is highlighted in a different color.",
  },
  code2ImageAlt: {
    id: "LangingPage.code2ImageAlt",
    defaultMessage:
      "An image split in half. The left side shows code with a comment indicating a workaround to a bug in a dependency. The right side shows the result of using HackStalker to analyze the code. There is a list of all GitHub links found in the code and information about the referenced issue fetched from GitHub (title, status, etc.).",
  },
});

const Heading = ({
  title,
  description,
}: {
  title: React.ReactNode;
  description: React.ReactNode;
}) => (
  <Row className="justify-content-center mb-3">
    <Col xs={12} md={6} className="text-center">
      <h2>{title}</h2>
      <p className="lead">{description}</p>
    </Col>
  </Row>
);

const slideImageWidth = 2500;
const slideImageHeight = slideImageWidth / 2;

const Slide = styled.div`
  img {
    width: 100%;
    height: 100%;

    object-fit: cover;
  }
`;

const Home = () => {
  const intl = useIntl();

  return (
    <PageContainer>
      <Header pageTitle={messages.pageTitle} />
      <MainContent>
        <Heading
          title={
            <FormattedMessage
              id="LangingPage.purposeTitle"
              defaultMessage="Track GitHub issues in your project dependencies"
            />
          }
          description={
            <FormattedMessage
              id="LangingPage.purposeParagraph"
              defaultMessage="Scan your repository for referenced issues and quickly find out if they're resolved or still haunting your code."
            />
          }
        />

        <Row className="justify-content-center mb-5">
          <Col xs={12} md={8}>
            <Carousel>
              <Carousel.Item>
                <Slide>
                  <Image
                    src="/landing-page/code-1.png"
                    width={slideImageWidth}
                    height={slideImageHeight}
                    alt={intl.formatMessage(messages.code1ImageAlt)}
                  />
                </Slide>
                <Carousel.Caption />
              </Carousel.Item>
              <Carousel.Item>
                <Slide>
                  <Image
                    src="/landing-page/code-2.png"
                    width={slideImageWidth}
                    height={slideImageHeight}
                    alt={intl.formatMessage(messages.code2ImageAlt)}
                  />
                </Slide>
                <Carousel.Caption />
              </Carousel.Item>
            </Carousel>
          </Col>
        </Row>

        <Heading
          title={<FormattedMessage id="LangingPage.tryNowTitle" defaultMessage="Try now!" />}
          description={
            <FormattedMessage
              id="LangingPage.tryNowParagraph"
              defaultMessage="Scan a public GitHub repository of your choice or check out the example analysis {here}."
              values={{
                here: (
                  <Link href="/github.com/hack-stalker/hack-stalker-demo">
                    <FormattedMessage id="LangingPage.here" defaultMessage="here" />
                  </Link>
                ),
              }}
            />
          }
        />

        <Row className="justify-content-center mb-5">
          <Col xs={12} md={8}>
            <GitHubRepoAnalysisForm />
          </Col>
        </Row>

        <Heading
          title={
            <FormattedMessage
              id="LangingPage.privateBetaTitle"
              defaultMessage="Join the Private Beta"
            />
          }
          description={
            <FormattedMessage
              id="LangingPage.privateBetaParagraph"
              defaultMessage="Get early access to private repo scanning and new features like notifications for issue updates."
            />
          }
        />

        <Row className="justify-content-center mb-5">
          <Col xs={12} md={8}>
            <div className="bg-primary p-4 rounded text-white">
              <PrivateBetaForm />
            </div>
          </Col>
        </Row>
      </MainContent>
      <Footer />
    </PageContainer>
  );
};

export default Home;
